// Simple namespaced storage with versioning and expiry

export type StoredValue<T> = {
    v: number; // schema version
    e?: number; // expiry epoch ms
    d: T; // data
};

export function makeNamespace(hostOrigin: string, userId: string, schemaVersion: number) {
    const origin = hostOrigin.replace(/[^a-z0-9._-]/gi, "_");
    const uid = userId.replace(/[^a-z0-9._-]/gi, "_");
    return {
        key: (suffix: string) => `public_widget:${origin}:${uid}:v${schemaVersion}:${suffix}`
    };
}

export function storageGet<T>(key: string): T | null {
    try {
        const raw = localStorage.getItem(key);
        if (!raw) return null;
        const parsed = JSON.parse(raw) as StoredValue<T>;
        if (parsed.e && Date.now() > parsed.e) {
            localStorage.removeItem(key);
            return null;
        }
        if (!parsed || typeof parsed !== "object" || !("d" in parsed)) return null;
        return parsed.d as T;
    } catch {
        return null;
    }
}

export function storageSet<T>(key: string, value: T, opts?: { schemaVersion?: number; ttlMs?: number }) {
    try {
        const v = opts?.schemaVersion ?? 1;
        const e = opts?.ttlMs ? Date.now() + opts.ttlMs : undefined;
        const wrapped: StoredValue<T> = { v, e, d: value };
        localStorage.setItem(key, JSON.stringify(wrapped));
    } catch {}
}

export function getOrCreateBrowserId(): string {
    const KEY = "public_widget_browser_id";
    try {
        let id = localStorage.getItem(KEY);
        if (!id) {
            id = `${Date.now().toString(36)}_${Math.random().toString(36).slice(2, 10)}`;
            localStorage.setItem(KEY, id);
        }
        return id;
    } catch {
        // Fallback ephemeral
        return `${Date.now().toString(36)}_${Math.random().toString(36).slice(2, 10)}`;
    }
}
